import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal, ClipReveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { CTABand } from '@/components/site/CTABand';
import { CityStrip } from '@/components/site/CityStrip';
import { Counter } from '@/components/site/Counter';
import { WRAP, SECTION, H2, H2_SM } from '@/components/site/ServicePageParts';
import { PROCESS, STATS } from '@/data/site';
import { ABOUT_IMAGE } from '@/data/homeImages';
import { motionOn } from '@/lib/motion';

gsap.registerPlugin(ScrollTrigger);

const PEOPLE = [
  { name: 'Aniket Bose', role: 'Founding partner · Strategy', location: 'Pune' },
  { name: 'Riya Shenoy', role: 'Design lead', location: 'Bengaluru' },
  { name: 'Devansh Khurana', role: 'Engineering lead', location: 'Pune' },
  { name: 'Kavya Iyer', role: 'Motion & interaction', location: 'Mumbai' },
  { name: 'Faizan Sheikh', role: 'AI / Platform', location: 'Hyderabad' },
  { name: 'Manjari Rao', role: 'Producer · Ops', location: 'Pune' },
];

const PRINCIPLES = [
  { k: 'Restraint', v: 'Most products fail by addition. We design by subtraction—if a screen survives the “does this earn its place?” review, it stays.' },
  { k: 'Numbered briefs', v: 'Every engagement starts with a one-page brief and three measurable outcomes. Vague success criteria are an unpaid invoice waiting to happen.' },
  { k: 'In the file', v: 'Designers and engineers work in the same file, in the same week. No throw-overs, no “handoff” meetings.' },
  { k: 'Quiet motion', v: 'Animation should make a product feel inevitable, not impressive. If a motion needs to be explained, it doesn’t belong.' },
  { k: 'Stay close', v: 'After launch we stay for a month. Bugs, polish, the second batch of analytics—we own them with you.' },
  { k: 'Say no', v: 'We turn down ~70% of inbound. Not arrogance—just a hard belief that taking on the wrong work makes the right work worse.' },
];

const initials = (n) => n.split(' ').map((w) => w[0]).join('');

export default function About() {
  const root = useRef(null);

  useEffect(() => {
    if (!motionOn() || !root.current) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo('.ab-img', { yPercent: -7 }, {
        yPercent: 7,
        ease: 'none',
        scrollTrigger: { trigger: '.ab-media', start: 'top bottom', end: 'bottom top', scrub: true },
      });
      const cards = gsap.utils.toArray('.ab-step');
      const mm = gsap.matchMedia();
      mm.add('(min-width: 768px)', () => {
        cards.forEach((c, i) => {
          if (i === cards.length - 1) return;
          gsap.to(c, {
            scale: 0.94,
            filter: 'brightness(.6)',
            ease: 'none',
            scrollTrigger: { trigger: cards[i + 1], start: 'top 85%', end: 'top 30%', scrub: true },
          });
        });
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={root} data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-[12vw] -top-[18vw] h-[70vw] w-[70vw] max-h-[1000px] max-w-[1000px] rounded-full blur-[20px]"
          style={{ background: 'radial-gradient(circle at 40% 45%, rgba(108,92,231,.32), rgba(75,159,255,.12) 40%, transparent 68%)' }}
        />
        <div className={`relative ${WRAP} pt-28 md:pt-40 pb-20 md:pb-28`}>
          <SectionLabel as="h1" num="01" data-testid="about-eyebrow">
            About NioDelta — Software Development Studio Serving US Clients
          </SectionLabel>
          <WordReveal
            as="p"
            text="Twelve operators. One opinionated way of building."
            accent="opinionated"
            data-testid="about-headline"
            className="mt-8 max-w-[15ch] font-display font-semibold text-[clamp(44px,7.4vw,128px)] leading-[0.92] tracking-[-0.05em]"
          />
          <div className="mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 border-t border-white/[0.08] pt-10">
            <div className="lg:col-span-5">
              <Reveal delay={300}>
                <p className="text-lg md:text-xl text-[#C9C9D3] leading-relaxed max-w-[46ch]">
                  NioDelta started in 2017 as a two-person studio out of a flat in Kothrud, Pune. Eight years
                  later we’re twelve operators across three cities, building products for founders we like and
                  saying no to most of the rest.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal delay={420}>
                <p className="text-lg md:text-xl text-ink-muted leading-relaxed max-w-[46ch]">
                  We are not an agency. We don’t have a sales team, account managers, or a deck library. We have
                  a partner who picks up the phone, a brief that gets shorter every quarter, and a builder’s
                  bias against PowerPoint.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* STUDIO IMAGE BAND */}
      <section data-testid="about-image" className={`relative ${WRAP} pb-20 md:pb-28`}>
        <ClipReveal className="ab-media relative aspect-[4/3] md:aspect-[21/9] overflow-hidden rounded-[18px] ring-1 ring-white/[0.08]">
          <img
            className="ab-img absolute -inset-y-[10%] inset-x-0 h-[120%] w-full object-cover [filter:grayscale(.85)_contrast(1.05)_brightness(.8)]"
            src={ABOUT_IMAGE.src}
            alt={ABOUT_IMAGE.alt}
            loading="lazy"
            referrerPolicy="no-referrer"
          />
          <div aria-hidden className="absolute inset-0 bg-[linear-gradient(200deg,rgba(108,92,231,.32),transparent_50%),linear-gradient(0deg,rgba(10,10,15,.75),transparent_45%)]" />
          <div className="nd-mono absolute left-5 right-5 md:left-8 md:right-8 bottom-5 md:bottom-7 flex items-center justify-between gap-4">
            <span className="flex items-center gap-2.5 text-white"><span className="nd-live" aria-hidden />Kothrud, Pune · since 2017</span>
            <span className="hidden sm:inline">The studio</span>
          </div>
        </ClipReveal>
      </section>

      {/* STATS */}
      <section data-testid="about-stats" className="relative border-t border-white/[0.08]">
        <div className={`${WRAP} grid grid-cols-2 lg:grid-cols-4`}>
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 80}>
              <div className={`h-full py-12 md:py-16 pr-4 border-white/[0.08] ${i % 2 ? 'pl-5 border-l' : ''} ${i > 1 ? 'max-lg:border-t lg:pl-5 lg:border-l' : ''}`}>
                <div className="font-display font-medium text-[clamp(56px,7vw,120px)] tracking-[-0.055em] leading-none">
                  <Counter value={parseFloat(s.kpi)} decimals={s.kpi.includes('.') ? 1 : 0} />
                  <span className="nd-accent">{s.suffix}</span>
                </div>
                <div className="mt-5 nd-mono">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className={SECTION}>
        <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <SectionLabel num="02">Principles</SectionLabel>
              <Reveal>
                <h2 className={`mt-7 ${H2_SM}`}>
                  Six things we <span className="italic text-electric">do not</span> negotiate.
                </h2>
              </Reveal>
            </div>
          </div>
          <ol className="lg:col-span-8 border-t border-white/[0.08]">
            {PRINCIPLES.map((p, i) => (
              <Reveal as="li" key={p.k} delay={i * 60} className="border-b border-white/[0.08]">
                <div className="group grid grid-cols-[56px_1fr] md:grid-cols-[72px_minmax(0,0.9fr)_minmax(0,1.3fr)] gap-x-6 gap-y-3 py-8 md:py-11">
                  <span className="nd-mono pt-2 tabular-nums">0{i + 1}</span>
                  <h3 className="font-display text-[28px] md:text-[40px] font-semibold tracking-[-0.04em] leading-none transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:translate-x-2">
                    {p.k}
                  </h3>
                  <p className="col-start-2 md:col-start-3 text-ink-muted leading-relaxed max-w-[52ch]">{p.v}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* PROCESS */}
      <section data-testid="about-process" className={SECTION}>
        <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <SectionLabel num="03">Process</SectionLabel>
              <Reveal>
                <h2 className={`mt-7 ${H2_SM}`}>
                  Five stages, <span className="italic text-violet">one file.</span>
                </h2>
              </Reveal>
              <Reveal delay={100}>
                <p className="mt-6 text-ink-muted leading-relaxed max-w-sm">
                  We use the same five-stage rhythm whether the engagement is six weeks or six months. The
                  shape of the work is constant; only the depth changes.
                </p>
              </Reveal>
            </div>
          </div>
          <ol className="lg:col-span-8 lg:col-start-5">
            {PROCESS.map((p, i) => (
              <li
                key={p.step}
                className="ab-step md:sticky mb-4 origin-top rounded-[18px] border border-white/[0.08] bg-ink-surface p-7 md:p-10 shadow-[0_-24px_48px_-24px_rgba(5,5,12,.95)]"
                style={{ top: `${120 + i * 18}px` }}
              >
                <div className="flex items-start justify-between gap-6">
                  <h3 className="font-display text-2xl md:text-[34px] font-semibold tracking-[-0.035em] leading-tight">{p.title}</h3>
                  <span className="font-display font-medium text-[56px] md:text-[88px] leading-[0.8] tracking-[-0.06em] text-white/[0.12] tabular-nums">{p.step}</span>
                </div>
                <p className="mt-6 text-ink-muted leading-relaxed max-w-2xl">{p.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PEOPLE */}
      <section data-testid="about-people" className={SECTION}>
        <div className={WRAP}>
          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-8 mb-14 md:mb-20">
            <SectionLabel num="04" className="lg:pt-4">People</SectionLabel>
            <Reveal>
              <h2 className={`${H2} max-w-[18ch]`}>
                A small bench. <span className="italic text-electric">No subcontractors.</span>
              </h2>
            </Reveal>
          </div>
          <ul className="border-t border-white/[0.08]">
            {PEOPLE.map((p, i) => (
              <Reveal as="li" key={p.name} delay={i * 60} className="border-b border-white/[0.08]">
                <div
                  data-testid={`person-${p.name.split(' ')[0].toLowerCase()}`}
                  className="group relative isolate grid grid-cols-[auto_1fr] lg:grid-cols-[220px_auto_1fr_auto] gap-x-6 lg:gap-x-8 gap-y-2 items-center py-7 md:py-9"
                >
                  <span aria-hidden className="absolute inset-y-0 -inset-x-4 md:-inset-x-12 -z-10 origin-bottom scale-y-0 bg-[linear-gradient(90deg,rgba(108,92,231,.14),rgba(75,159,255,.04))] transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:scale-y-100" />
                  <span className="nd-mono hidden lg:block tabular-nums">{String(i + 1).padStart(2, '0')} / {String(PEOPLE.length).padStart(2, '0')}</span>
                  <span
                    aria-hidden
                    className="row-span-2 lg:row-span-1 h-14 w-14 md:h-16 md:w-16 rounded-full ring-1 ring-white/10 grid place-items-center font-display font-semibold text-sm tracking-tight text-white/80"
                    style={{
                      background: `radial-gradient(circle at ${20 + i * 10}% ${30 + i * 8}%, rgba(108,92,231,0.55), transparent 60%), radial-gradient(circle at 80% 80%, rgba(75,159,255,0.4), transparent 55%), #0E0E15`,
                    }}
                  >
                    {initials(p.name)}
                  </span>
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-[clamp(26px,3.2vw,48px)] tracking-[-0.045em] leading-none transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:translate-x-3">
                      {p.name}
                    </div>
                    <div className="mt-2 text-ink-muted text-[15px]">{p.role}</div>
                  </div>
                  <span className="nd-mono col-start-2 lg:col-start-auto lg:text-right">{p.location}</span>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <CityStrip num="05" />

      <CTABand
        eyebrow="Working in public"
        headline="Want to know the people before the pitch?"
        accent="before the pitch?"
        sub="The discovery call is with a partner. No SDR. No account manager. The person on the call is the person on the project."
      />
    </div>
  );
}
